/**
 * LookupPanelIframe — the CRM lookup panel rendered as an iframe of the
 * standalone LookUp app, instead of the inline `LookupPanel` component.
 *
 * Used for the SaaS codes listed in `LOOKUP_IFRAME_SAAS`. The point is OAuth
 * isolation: LookUp has its own OAuth client per CRM (e.g. HubSpot client
 * `870ca14e-…` in `modules/tpa/lookup/lookup-types.ts`), so running it under
 * its own `appCode=lookupfor<saas>` lets the user authorize LookUp separately
 * from the messaging app it is embedded in. Same origin, so the Firebase
 * session and localStorage carry over — only the CRM token differs.
 *
 * Mirrors legacy `renderLookUpViewInsideConv` / `uaConvToLookupIframe`
 * (public/legacy/js/firebase-things.js:12388, :34625).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useAppStore } from '@/stores/app-store';
import { useUIStore } from '@/stores/ui-store';
import type { Conversation } from '@/types/messaging';

interface LookupPanelIframeProps {
  conversation: Conversation;
}

export function LookupPanelIframe({ conversation }: LookupPanelIframeProps) {
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const setLookupPanelOpen = useUIStore((s) => s.setLookupPanelOpen);

  const frameRef = useRef<HTMLIFrameElement>(null);
  // The child only starts listening for `contactChange` once its CRM auth
  // resolves, so we can't post on `load` — we wait for its ready ping.
  const readyRef = useRef(false);
  // Same signal as `readyRef`, but as state: until the child reports in we
  // render our own close button, so a frame that never loads (offline, chunk
  // error, auth redirect) can't trap the user with an unclosable panel.
  const [childReady, setChildReady] = useState(false);

  const phone = conversation.contactPhone;
  // Kept in a ref so the ready handler can read the number that is current
  // when the ping lands, without re-subscribing the listener on every switch.
  const phoneRef = useRef(phone);
  useEffect(() => { phoneRef.current = phone; }, [phone]);

  const frameId = `lookup-handleFrame-${appCodeSAAS}`;

  // Built once per SaaS — switching conversations must not reload the iframe
  // (that would drop the loaded record and re-run the auth check every time).
  //
  // `forceExpanded=1` tells the child to ignore its stored minimized state.
  // That state lives in the origin-wide `ua-minimizeAppIframe` key, shared
  // with the extension-injected LookUp panel — so we ask through the URL
  // instead of overwriting a value that isn't only ours.
  const src = useMemo(
    () =>
      `${window.location.origin}/app?appCode=lookupfor${appCodeSAAS}` +
      `&iframeId=${encodeURIComponent(`lookup-handleFrame-${appCodeSAAS}`)}` +
      `&forceExpanded=1`,
    [appCodeSAAS]
  );

  const postContact = useCallback((phoneNumber: string) => {
    frameRef.current?.contentWindow?.postMessage(
      { type: 'contactChange', phoneNumber },
      window.location.origin
    );
  }, []);

  // Child → parent messages
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === 'lookupIframeReady') {
        readyRef.current = true;
        setChildReady(true);
        postContact(phoneRef.current);
        return;
      }
      // The child's collapse button and its "close" on the authorize prompt
      // both mean the same thing here: give the chat its width back.
      if (msg.type === 'minimizeAppIframe' || msg.type === 'removeLookupApp') {
        setLookupPanelOpen(false);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [postContact, setLookupPanelOpen]);

  // Conversation switched — tell the already-loaded iframe to look up the new
  // number. Before the ready ping arrives the `lookupIframeReady` branch above
  // sends the current number instead, so nothing is lost.
  //
  // Posted even when the new conversation has no phone: the empty string is
  // what tells the child to clear, otherwise the previous contact's record
  // stays on screen next to a chat it doesn't belong to.
  useEffect(() => {
    if (!readyRef.current) return;
    postContact(phone);
  }, [phone, postContact]);

  return (
    <div
      data-testid="lookup-panel-iframe"
      className="w-80 shrink-0 border-l border-border bg-card flex flex-col h-full min-h-0 overflow-hidden"
    >
      {/* Our own close button, shown only until the child's header (which has
          its own collapse button) is up. Without it a frame that never
          finishes loading leaves the panel open with no way to dismiss it —
          the chat header hides its lookup toggle while the panel is open. */}
      {!childReady && (
        <div className="flex items-center gap-2 px-3 py-2 shrink-0 border-b border-border">
          <span className="text-xs text-muted-foreground">Loading CRM LookUp…</span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setLookupPanelOpen(false)}
            title="Hide lookup"
            aria-label="Hide lookup"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <iframe
        ref={frameRef}
        id={frameId}
        src={src}
        title="CRM LookUp"
        className="flex-1 min-h-0 w-full border-0"
      />
    </div>
  );
}
